import type { SxProps, Theme } from '@mui/material'

export const LOGIN_FONTS = {
    sans: '"Work Sans", sans-serif',
    mono: '"JetBrains Mono", ui-monospace, SFMono-Regular, monospace',
} as const

export const LOGIN_ACCENT = 'rgb(86, 153, 239)'
/** Official Google Sign-In outline button label color */
export const LOGIN_GOOGLE_TEXT = '#1F1F1F'

export type LoginPalette = {
    bg: string
    surface: string
    surface2: string
    border: string
    accent: string
    accentSoft: string
    fg: string
    mute: string
}

export function getLoginPalette(isDark: boolean): LoginPalette {
    if (isDark) {
        return {
            bg: 'rgb(14, 14, 14)',
            surface: 'rgb(22, 22, 22)',
            surface2: 'rgb(32, 32, 32)',
            border: 'rgb(58, 58, 58)',
            accent: LOGIN_ACCENT,
            accentSoft: 'rgba(86, 153, 239, 0.14)',
            fg: 'rgb(232, 232, 232)',
            mute: 'rgb(140, 140, 140)',
        }
    }
    return {
        bg: '#ffffff',
        surface: '#f6f6f6',
        surface2: '#eeeeee',
        border: '#d8d8d8',
        accent: LOGIN_ACCENT,
        accentSoft: 'rgba(86, 153, 239, 0.10)',
        fg: 'rgb(20, 20, 20)',
        mute: 'rgb(110, 110, 110)',
    }
}

export function loginGhostButtonSx(palette: LoginPalette): SxProps<Theme> {
    return {
        borderRadius: '4px !important',
        minHeight: '40px !important',
        maxHeight: '40px !important',
        border: `1px solid ${palette.border} !important`,
        backgroundColor: `${palette.bg === '#ffffff' ? '#ffffff' : palette.surface} !important`,
        color: `${palette.bg === '#ffffff' ? LOGIN_GOOGLE_TEXT : palette.fg} !important`,
        textTransform: 'none',
        boxShadow: 'none !important',
        fontSize: '14px',
        fontWeight: 500,
        '&:hover': {
            borderColor: `${palette.accent} !important`,
            backgroundColor: `${palette.bg === '#ffffff' ? '#ffffff' : palette.surface2} !important`,
        },
    }
}

export const loginPrimaryButtonSx: SxProps<Theme> = {
    borderRadius: '4px !important',
    minHeight: '40px !important',
    maxHeight: '40px !important',
    backgroundColor: `${LOGIN_ACCENT} !important`,
    color: '#000 !important',
    boxShadow: 'none !important',
    border: 'none !important',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '14px',
    '&:hover': {
        backgroundColor: '#ffffff !important',
        color: '#000 !important',
        boxShadow: 'none !important',
    },
    '&.Mui-disabled': {
        backgroundColor: 'rgba(86, 153, 239, 0.35) !important',
        color: 'rgba(0, 0, 0, 0.45) !important',
    },
}

export function loginLinkSx(palette: LoginPalette): SxProps<Theme> {
    return {
        color: palette.accent,
        textDecoration: 'none',
        cursor: 'pointer',
        fontSize: '0.875rem',
        '&:hover': { color: palette.fg, textDecoration: 'none' },
    }
}

export function loginOtpSx(palette: LoginPalette): SxProps<Theme> {
    return {
        width: '100%',
        maxWidth: '408px',
        alignSelf: 'center',
        gap: '8px',
        '& .MuiOutlinedInput-root': {
            height: '40px',
            borderRadius: '4px !important',
            backgroundColor: palette.surface,
            '& fieldset': { borderRadius: '4px !important', borderColor: palette.border },
            '&:hover fieldset': { borderColor: palette.accent },
            '&.Mui-focused fieldset': { borderColor: palette.accent, borderWidth: '1px' },
        },
        '& input': {
            color: palette.fg,
            fontFamily: `${LOGIN_FONTS.mono} !important`,
            fontSize: '1.15rem',
        },
    }
}
